import posthog, { type CaptureResult } from 'posthog-js'
import publicConfig from '~/utils/client/publicConfig'
import { environment, isDevelopment } from '~/utils/shared/platform'
import { sanitizeAnalyticsProperties } from './analyticsPrivacy'
import type {
  AnalyticsEventName,
  AnalyticsEventProperties,
  AnalyticsProperties,
  AnalyticsScreenName,
} from './analyticsTypes'

const blockedAutomaticPostHogEvents = new Set([
  '$autocapture',
  '$copy_autocapture',
  '$dead_click',
  '$exception',
  '$heatmaps_data',
  '$pageleave',
  '$pageview',
  '$rageclick',
  '$web_vitals',
  '$$heatmap',
])

let hasInitializedAnalyticsClient = false

function getAnalyticsConfiguration() {
  return publicConfig()?.services?.posthog ?? {}
}

function getAnalyticsToken() {
  return getAnalyticsConfiguration().token || ''
}

function getAnalyticsHost() {
  return getAnalyticsConfiguration().host || 'https://us.i.posthog.com'
}

function isTestEnvironment() {
  return process.env.NODE_ENV === 'test'
}

export function getAnalyticsOrganizationId() {
  return getAnalyticsConfiguration().organizationId || 'sebrae-nacional'
}

export function buildDefaultAnalyticsEventProperties() {
  return {
    app_environment: environment,
    organization_id: getAnalyticsOrganizationId(),
  }
}

export function registerDefaultAnalyticsEventProperties(
  analyticsClient: Pick<typeof posthog, 'register'>,
) {
  analyticsClient.register(buildDefaultAnalyticsEventProperties())
}

export function isAnalyticsConfigured() {
  return !isTestEnvironment() && Boolean(getAnalyticsToken())
}

function filterPostHogEvent(captureResult: CaptureResult | null) {
  if (!captureResult) {
    return null
  }

  return blockedAutomaticPostHogEvents.has(String(captureResult.event)) ? null : captureResult
}

export function initializeAnalyticsClient() {
  if (hasInitializedAnalyticsClient || !isAnalyticsConfigured()) {
    return false
  }

  posthog.init(getAnalyticsToken(), {
    api_host: getAnalyticsHost(),
    defaults: '2026-01-30',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    capture_dead_clicks: false,
    capture_exceptions: false,
    capture_heatmaps: false,
    debug: isDevelopment,
    disable_session_recording: true,
    enable_recording_console_log: false,
    mask_all_element_attributes: true,
    mask_all_text: true,
    mask_personal_data_properties: true,
    custom_personal_data_properties: [
      'address',
      'cnpj',
      'coordinates',
      'cpf',
      'email',
      'latitude',
      'longitude',
      'name',
      'prompt',
      'query',
      'search',
      'text',
    ],
    property_denylist: [
      'address',
      'cnpj',
      'coordinates',
      'cpf',
      'email',
      'latitude',
      'longitude',
      'name',
      'prompt',
      'query',
      'search',
      'text',
    ],
    session_recording: {
      blockSelector: '[data-sensitive], .ph-no-capture, .ph-no-record',
      maskAllInputs: true,
      maskInputOptions: {
        email: true,
        password: true,
        search: true,
        select: true,
        tel: true,
        text: true,
        textarea: true,
        url: true,
      },
      maskTextSelector: 'body',
      recordBody: false,
      recordHeaders: false,
    },
    before_send: filterPostHogEvent,
    loaded: posthogClient => {
      registerDefaultAnalyticsEventProperties(posthogClient)
    },
  })

  hasInitializedAnalyticsClient = true
  return true
}

export function getAnalyticsClient() {
  return isAnalyticsConfigured() && hasInitializedAnalyticsClient ? posthog : null
}

export function trackAnalyticsEvent<EventName extends AnalyticsEventName>(
  eventName: EventName,
  properties?: AnalyticsEventProperties[EventName],
) {
  const analyticsClient = getAnalyticsClient()
  if (!analyticsClient) {
    return
  }

  const sanitizedProperties = sanitizeAnalyticsProperties(eventName, {
    organization_id: getAnalyticsOrganizationId(),
    ...properties,
  } as AnalyticsEventProperties[EventName])

  analyticsClient.capture(eventName, sanitizedProperties)
}

export function trackScreenViewed(
  screenName: AnalyticsScreenName,
  properties?: Omit<AnalyticsProperties, 'screen'>,
) {
  trackAnalyticsEvent('screen_viewed', {
    ...properties,
    screen: screenName,
  })
}
